import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { tokens } from '../../../global/theme/tokens';
import ExoExplorer from '../../exo/ExoExplorer';
import MediaPreview from '../../exo/explorer/MediaPreview';
import ExoDialog from '../../exo/ExoDialog';
import LabeledBox from '../../special/LabeledBox';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const TypeAttachments = ({
  updateValidationOnChange,
  dataCollection,
  field,
  size,
  errors,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [open, setOpen] = useState(false);

  const handleSelect = (files) => {
    updateValidationOnChange(field.key, files);
    setOpen(false);
  };

  const handleRemoveItem = (index) => {
    const currentFiles = dataCollection[field.key] || [];
    const newFiles = currentFiles.filter((_, i) => i !== index);
    updateValidationOnChange(field.key, newFiles.length > 0 ? newFiles : null);
  };

  const handleReset = () => {
    updateValidationOnChange(field.key, null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const files = dataCollection[field.key] || [];

  return (
    <Box className={`w-full ${field.disabled ? 'opacity-70' : ''}`}>
      <LabeledBox
        size={size}
        label={`${field.label}${field.maxItems ? ` (max. ${field.maxItems})` : ''}`}
        sx={{ padding: files.length > 0 ? '8px' : undefined }}
        className="w-full"
      >
        {files.length > 0 ? (
          <Box className="flex flex-col gap-2 w-full">
            {files.map((file, index) => (
              <Box
                key={file.id || index}
                className="flex items-center gap-3 p-2 rounded-lg"
                sx={{
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.bgInput}`,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.bgInput,
                  }}
                >
                  {file.mimeType?.startsWith('image/') ? (
                    <MediaPreview
                      src={file.temporaryUrl || file.thumbnailUrl}
                      onlyIcons={false}
                      size="40px"
                    />
                  ) : (
                    <InsertDriveFileIcon sx={{ color: colors.text, opacity: 0.6 }} />
                  )}
                </Box>
                <Box className="flex-1 min-w-0">
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 500, color: colors.text }}
                  >
                    {file.title || file.name || 'Unnamed file'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text, opacity: 0.6 }}
                  >
                    {formatFileSize(file.fileSize)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveItem(index)}
                  sx={{
                    color: colors.text,
                    opacity: 0.5,
                    '&:hover': {
                      opacity: 1,
                      color: colors.error,
                      backgroundColor: colors.error + '20',
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{
                borderStyle: 'dashed',
                color: colors.text,
                borderColor: colors.bgInput,
                '&:hover': {
                  borderColor: colors.info,
                  backgroundColor: colors.info + '10',
                },
              }}
            >
              Weitere hinzufügen
            </Button>
          </Box>
        ) : (
          <Box
            className="flex items-center justify-center cursor-pointer py-4"
            onClick={() => setOpen(true)}
          >
            <Typography variant="body2" sx={{ color: colors.text, opacity: 0.5 }}>
              Klicken zum Auswählen...
            </Typography>
          </Box>
        )}
      </LabeledBox>
      <ExoDialog open={open} onClose={() => setOpen(false)}>
        <ExoExplorer
          onSelect={handleSelect}
          multiSelect
          preSelected={dataCollection[field.key]}
          maxItems={field.maxItems}
          maxSizeKB={field.maxSizeKB}
          mediaType={field.mediaType}
        />
      </ExoDialog>
    </Box>
  );
};

export default TypeAttachments;
